// Phase MD-1 — Executive MD Dashboard
// Sections: A) Snapshot KPIs · B) Sales Funnel · C) Department Performance ·
//           D) Approvals/Attention · E) Top Performers · F) Risk Alerts
// Filters: range (today/week/month/quarter/year/custom) + department + manager
// Access: admin OR md role only (page-level guard + sidebar gate)

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  CurrencyInr, Trophy, FileText, Receipt, Users, ChartLine, Handshake,
  Buildings, Warning, ArrowsClockwise, Calendar as CalendarIcon, Funnel,
  ShieldWarning, Star, Package, ArrowRight, CheckCircle, ChartBar,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

// ------------ helpers ------------
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

// ------------ small UI primitives ------------
const KpiCard = ({ icon: Icon, label, value, money = false, tone = "blue", testId, onClick, helper }) => {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    rose:    "border-rose-200 bg-rose-50/40 text-rose-700",
    amber:   "border-amber-200 bg-amber-50/40 text-amber-700",
    blue:    "border-blue-200 bg-blue-50/40 text-blue-700",
    indigo:  "border-indigo-200 bg-indigo-50/40 text-indigo-700",
    slate:   "border-slate-200 bg-slate-50/40 text-slate-700",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`group text-left bg-white border ${toneMap[tone] || toneMap.blue} p-4 rounded-none shadow-sm hover:shadow-md transition-all duration-200 ${onClick ? "cursor-pointer hover:-translate-y-0.5" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between mb-2">
        <Icon size={20} weight="duotone" />
        {onClick && <ArrowRight size={12} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
        {money ? fmtINR(value) : fmtNum(value)}
      </div>
      {helper && <div className="text-[11px] text-slate-500 mt-0.5">{helper}</div>}
    </button>
  );
};

const RANGES = [
  { key: "today",   label: "Today" },
  { key: "week",    label: "This Week" },
  { key: "month",   label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "year",    label: "This Year" },
  { key: "all",     label: "All Time" },
  { key: "custom",  label: "Custom" },
];

const FILTER_BAR_TESTID = "md-filter-bar";

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const MDDashboard = ({ user, logout }) => {
  const navigate = useNavigate();

  // --- access guard (admin OR md role) ---
  const allowed = user?.role === "admin" || user?.role === "md";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [department, setDepartment] = useState("");
  const [managerId, setManagerId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("range", range);
      if (range === "custom" && startDate && endDate) {
        params.set("start_date", startDate);
        params.set("end_date", endDate);
      }
      if (department) params.set("department", department);
      if (managerId) params.set("manager_id", managerId);
      const r = await axios.get(`${API}/dashboard/md?${params.toString()}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load MD dashboard");
    } finally {
      setLoading(false);
    }
  }, [range, startDate, endDate, department, managerId]);

  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  // ---- derived ----
  const kpis = data?.kpis || {};
  const funnel = data?.funnel || [];
  const attention = data?.attention || {};
  const risks = data?.risks || {};
  const tops = data?.top_performers || {};
  const departments = data?.filters?.available_departments || [];
  const managers = data?.filters?.available_managers || [];

  // ---- Executive Narrative — auto-generated from existing kpis/risks/funnel ----
  const narrative = useMemo(() => {
    if (!data) return "";
    const k = data.kpis || {};
    const r = data.risks || {};
    const a = data.attention || {};
    const fu = data.funnel || [];
    const win = data.filters?.range || "month";
    const winLabel = ({ today: "today", week: "this week", month: "this month",
      quarter: "this quarter", year: "this year", all: "all-time", custom: "the selected range" })[win] || win;

    const fmt = (n) => {
      const v = Number(n || 0);
      if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
      if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
      if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
      return `₹${v.toFixed(0)}`;
    };

    const parts = [];
    // 1. Headline revenue
    if (k.total_revenue > 0) {
      parts.push(`Revenue ${winLabel} stands at ${fmt(k.total_revenue)} across ${k.invoices_raised || 0} invoices`);
    } else if ((k.total_leads || 0) > 0) {
      parts.push(`${k.total_leads} leads generated ${winLabel}`);
    } else {
      parts.push(`No invoiced revenue ${winLabel} yet`);
    }
    // 2. Pipeline / expected
    if ((k.expected_revenue || 0) > 0) {
      parts.push(`with ${fmt(k.expected_revenue)} expected from open pipeline`);
    }
    // 3. Conversion
    const quoteStage = fu.find((f) => f.stage === "Quotations");
    if (quoteStage && quoteStage.conv_pct >= 0 && (k.total_leads || 0) > 0) {
      parts.push(`lead-to-quote conversion at ${quoteStage.conv_pct}%`);
    }
    // 4. Risk emphasis (the "but" clause)
    const riskBits = [];
    if ((r.invoices_overdue || 0) > 0) riskBits.push(`${r.invoices_overdue} invoices totalling ${fmt(r.invoices_overdue_value)} overdue`);
    if ((r.leads_without_followup || 0) > 0) riskBits.push(`${r.leads_without_followup} leads without follow-up`);
    if ((r.stale_quotations_30d || 0) > 0) riskBits.push(`${r.stale_quotations_30d} stale quotations`);
    if ((a.discounts_pending_approval || 0) > 0) riskBits.push(`${a.discounts_pending_approval} discount approvals pending`);

    let sentence = parts.join(", ") + ".";
    if (riskBits.length > 0) {
      sentence = sentence.replace(/\.$/, "") + ` — but ${riskBits.slice(0, 3).join(", ")} require attention.`;
    } else {
      sentence = sentence.replace(/\.$/, ".") + " No critical risks flagged.";
    }
    return sentence;
  }, [data]);

  // Tone of narrative tile based on risk burden
  const narrativeTone = useMemo(() => {
    const r = data?.risks || {};
    const high = (r.invoices_overdue || 0) + (r.payments_overdue_30d || 0);
    if (high >= 5) return "rose";
    if (high >= 1) return "amber";
    return "emerald";
  }, [data]);

  const attentionTotal = useMemo(
    () => Object.values(attention).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0),
    [attention]
  );
  const riskTotal = useMemo(
    () => (risks.leads_without_followup || 0) + (risks.stale_quotations_30d || 0) + (risks.invoices_overdue || 0)
        + (risks.payments_overdue_30d || 0) + (risks.stock_shortage || 0) + (risks.service_delays || 0),
    [risks]
  );

  // --- access denied screen ---
  if (!allowed) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 ml-64">
          <div className="p-12 text-center" data-testid="md-access-denied">
            <ShieldWarning size={48} className="mx-auto text-rose-500 mb-4" weight="duotone" />
            <h1 className="text-3xl font-bold mb-2">Access Restricted</h1>
            <p className="text-slate-600">The MD Dashboard is available to Admin and MD roles only.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 min-w-0 overflow-x-hidden">
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Executive · Managing Director</div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                MD Dashboard
              </h1>
              <p className="text-base text-slate-600">Business health at a glance — sales, collection, risks, and approvals.</p>
            </div>
            <button
              data-testid="md-refresh-btn"
              onClick={load}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>

          {/* Filter bar */}
          <div data-testid={FILTER_BAR_TESTID} className="bg-white border border-slate-200 p-4 mb-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <CalendarIcon size={14} className="text-slate-500" />
              <div className="flex flex-wrap gap-1">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    data-testid={`md-range-${r.key}`}
                    onClick={() => setRange(r.key)}
                    className={`rounded-none px-3 py-1.5 text-xs uppercase tracking-wider font-bold border transition-colors ${
                      range === r.key
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-white text-slate-700 border-slate-300 hover:border-[#002FA7]"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {range === "custom" && (
                <div className="flex items-center gap-2 ml-2">
                  <input data-testid="md-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="rounded-none border border-slate-300 text-xs p-1.5 outline-none focus:border-[#002FA7]" />
                  <span className="text-slate-400">→</span>
                  <input data-testid="md-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="rounded-none border border-slate-300 text-xs p-1.5 outline-none focus:border-[#002FA7]" />
                </div>
              )}
              <div className="ml-auto flex flex-wrap gap-2">
                <select data-testid="md-filter-dept" value={department} onChange={(e) => setDepartment(e.target.value)}
                  className="rounded-none border border-slate-300 text-xs p-1.5 outline-none focus:border-[#002FA7]">
                  <option value="">All Departments</option>
                  {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select data-testid="md-filter-manager" value={managerId} onChange={(e) => setManagerId(e.target.value)}
                  className="rounded-none border border-slate-300 text-xs p-1.5 outline-none focus:border-[#002FA7]">
                  <option value="">All Managers</option>
                  {managers.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <>
              {/* Executive Narrative Tile — 1-sentence summary, auto-generated */}
              <div data-testid="md-narrative-tile" className={`mb-6 border-l-4 p-5 shadow-sm bg-white ${
                narrativeTone === "rose"
                  ? "border-rose-500 bg-rose-50/40"
                  : narrativeTone === "amber"
                  ? "border-amber-500 bg-amber-50/40"
                  : "border-emerald-500 bg-emerald-50/40"
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 shrink-0 rounded-full w-8 h-8 flex items-center justify-center ${
                    narrativeTone === "rose" ? "bg-rose-100 text-rose-600" :
                    narrativeTone === "amber" ? "bg-amber-100 text-amber-600" :
                    "bg-emerald-100 text-emerald-600"
                  }`}>
                    {narrativeTone === "rose" ? <ShieldWarning size={16} weight="duotone" /> :
                     narrativeTone === "amber" ? <Warning size={16} weight="duotone" /> :
                     <CheckCircle size={16} weight="duotone" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-1">Executive Summary</div>
                    <p data-testid="md-narrative-text" className="text-base sm:text-[17px] leading-relaxed text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {narrative || "Loading…"}
                    </p>
                  </div>
                </div>
              </div>

              {/* SECTION A — Snapshot Cards */}
              <SectionHeader icon={ChartLine} title="Business Snapshot" />
              <div data-testid="md-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-8">
                <KpiCard testId="md-kpi-total_revenue"      icon={CurrencyInr} tone="emerald" money label="Total Revenue"     value={kpis.total_revenue}      onClick={() => navigate("/billing?tab=invoices")} />
                <KpiCard testId="md-kpi-total_collected"    icon={CheckCircle} tone="emerald" money label="Total Collection"  value={kpis.total_collected}    onClick={() => navigate("/billing?tab=payments")} />
                <KpiCard testId="md-kpi-pending_collection" icon={Warning}     tone="amber"   money label="Pending Collection" value={kpis.pending_collection} onClick={() => navigate("/billing?tab=aging")} />
                <KpiCard testId="md-kpi-total_leads"        icon={Users}       tone="blue"          label="Total Leads"       value={kpis.total_leads}        onClick={() => navigate("/leads")} />
                <KpiCard testId="md-kpi-quotations_sent"    icon={FileText}    tone="blue"          label="Quotations Sent"   value={kpis.quotations_sent}    onClick={() => navigate("/billing?tab=estimations")} />
                <KpiCard testId="md-kpi-sales_orders"       icon={Handshake}   tone="indigo"        label="Sales Orders"      value={kpis.sales_orders}       onClick={() => navigate("/billing?tab=sales-orders")} />
                <KpiCard testId="md-kpi-invoices_raised"    icon={Receipt}     tone="indigo"        label="Invoices Raised"   value={kpis.invoices_raised}    onClick={() => navigate("/billing?tab=invoices")} />
                <KpiCard testId="md-kpi-won_deals"          icon={Trophy}      tone="emerald"       label="Won Deals"         value={kpis.won_deals}          onClick={() => navigate("/crm/deals")} />
                <KpiCard testId="md-kpi-lost_deals"         icon={Warning}     tone="rose"          label="Lost Deals"        value={kpis.lost_deals}         onClick={() => navigate("/crm/deals")} />
                <KpiCard testId="md-kpi-expected_revenue"   icon={ChartLine}   tone="indigo"  money label="Expected Revenue"  value={kpis.expected_revenue} helper="probability-weighted pipeline" />
              </div>

              {/* SECTION B — Sales Funnel */}
              <SectionHeader icon={Funnel} title="Sales Funnel" subtitle="Lead → Quotation → Sales Order → Invoice → Payment" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <div className="lg:col-span-2 bg-white border border-slate-200 p-5 shadow-sm">
                  <div data-testid="md-funnel-chart" style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={funnel} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {funnel.map((_, i) => (
                            <Cell key={i} fill={["#002FA7", "#0EA5E9", "#7C3AED", "#10B981", "#F59E0B"][i] || "#002FA7"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 p-5 shadow-sm">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <th className="py-2">Stage</th>
                        <th className="py-2 text-right">Count</th>
                        <th className="py-2 text-right">Conv %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.map((f, i) => (
                        <tr key={f.stage} data-testid={`md-funnel-row-${i}`} className="border-b border-slate-100">
                          <td className="py-2 font-medium">{f.stage}</td>
                          <td className="py-2 text-right tabular-nums">{fmtNum(f.count)}</td>
                          <td className="py-2 text-right tabular-nums">
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${f.conv_pct >= 60 ? "bg-emerald-100 text-emerald-700" : f.conv_pct >= 30 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                              {f.conv_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SECTION C — Department Performance + D — Attention */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                <div data-testid="md-dept-perf" className="bg-white border border-slate-200 p-5 shadow-sm">
                  <SubHeader icon={Buildings} title="Department Performance" onMore={() => navigate("/team-performance")} />
                  {(data?.by_department || []).length === 0 ? (
                    <EmptyState text="No leads assigned across departments yet." />
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="py-2">Department</th>
                          <th className="py-2 text-right">Leads</th>
                          <th className="py-2 text-right">Quotes</th>
                          <th className="py-2 text-right">Conv %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.by_department || []).map((r, i) => {
                          const pct = r.leads ? Math.round((r.converted / r.leads) * 100) : 0;
                          return (
                            <tr key={r.department} data-testid={`md-dept-row-${i}`} className="border-b border-slate-100">
                              <td className="py-2 font-medium">{r.department}</td>
                              <td className="py-2 text-right tabular-nums">{r.leads}</td>
                              <td className="py-2 text-right tabular-nums">{r.quotations || 0}</td>
                              <td className="py-2 text-right tabular-nums text-emerald-700">{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div data-testid="md-attention" className="bg-white border-2 border-amber-300 p-5 shadow-sm">
                  <SubHeader icon={Warning} title="Attention Required" badge={attentionTotal} onMore={() => navigate("/approvals")} />
                  <ul className="space-y-2 text-sm">
                    <AttentionRow testId="md-att-quote-approval"   label="Quotations pending approval"  value={attention.quotations_pending_approval}  onClick={() => navigate("/approvals?tab=billing")} />
                    <AttentionRow testId="md-att-discount-approval" label="Discounts pending approval"   value={attention.discounts_pending_approval}   onClick={() => navigate("/approvals?tab=billing")} />
                    <AttentionRow testId="md-att-high-value"       label="High-value invoices (≥ ₹5L)"   value={attention.high_value_invoices}          onClick={() => navigate("/billing?tab=invoices")} />
                    <AttentionRow testId="md-att-overdue-coll"     label={`Overdue collections (${fmtINR(attention.pending_collections_value)})`} value={attention.pending_collections_count} onClick={() => navigate("/billing?tab=aging")} tone="rose" />
                    <AttentionRow testId="md-att-delayed-delivery" label="Delayed delivery"              value={attention.delayed_delivery}             onClick={() => navigate("/billing?tab=sales-orders")} />
                    <AttentionRow testId="md-att-amc"              label="AMC / service escalations"     value={attention.amc_service_escalations}      onClick={() => navigate("/service-tasks")} />
                  </ul>
                </div>
              </div>

              {/* SECTION E — Top Performers + F — Risk Alerts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                <div data-testid="md-top-performers" className="bg-white border border-slate-200 p-5 shadow-sm">
                  <SubHeader icon={Star} title="Top Performers" onMore={() => navigate("/team-performance")} />
                  <div className="space-y-3">
                    <PerformerCard testId="md-best-bde" label="Best BDE" person={tops.best_bde} icon={Trophy} />
                    <PerformerCard testId="md-best-bdm" label="Best BDM" person={tops.best_bdm} icon={Trophy} />
                    <ProductList testId="md-top-products-revenue" label="Top Products by Revenue" rows={tops.top_products_by_revenue || []} valueKey="revenue" money />
                    <ProductList testId="md-top-products-enquiry" label="Top Products by Enquiry" rows={tops.top_products_by_enquiry || []} valueKey="enquiries" />
                    <ProductList testId="md-top-customers" label="Top Customers" rows={(tops.top_customers || []).map((c) => ({ ...c, product_name: c.customer_name }))} valueKey="revenue" money />
                  </div>
                </div>

                <div data-testid="md-risks" className="bg-white border-2 border-rose-300 p-5 shadow-sm">
                  <SubHeader icon={ShieldWarning} title="Risk Alerts" badge={riskTotal} tone="rose" />
                  <ul className="space-y-2 text-sm">
                    <RiskRow testId="md-risk-no-followup" label="Leads without follow-up (>7 days)" value={risks.leads_without_followup} onClick={() => navigate("/leads")} />
                    <RiskRow testId="md-risk-stale-quotes" label="Quotations not converted (>30 days)" value={risks.stale_quotations_30d} onClick={() => navigate("/billing?tab=estimations")} />
                    <RiskRow testId="md-risk-invoices-overdue" label={`Invoices overdue (${fmtINR(risks.invoices_overdue_value)})`} value={risks.invoices_overdue} onClick={() => navigate("/billing?tab=aging")} />
                    <RiskRow testId="md-risk-payments-overdue" label="Payments overdue >30 days" value={risks.payments_overdue_30d} onClick={() => navigate("/billing?tab=aging")} />
                    <RiskRow testId="md-risk-stock-shortage" label="Stock shortage (out of stock)" value={risks.stock_shortage} onClick={() => navigate("/admin/catalogue")} />
                    <RiskRow testId="md-risk-service-delay" label="Service delays" value={risks.service_delays} onClick={() => navigate("/service-tasks")} />
                  </ul>
                </div>
              </div>

              <div className="text-center text-[11px] text-slate-400 pt-4 pb-8 border-t border-slate-200 mt-8">
                <ChartBar size={14} weight="duotone" className="inline mr-1 -mt-0.5" />
                MD Dashboard · Snapshot of {data?.filters?.range || "month"}
                {data?.filters?.window_start ? ` · ${data.filters.window_start.slice(0, 10)} → ${(data.filters.window_end || "").slice(0, 10)}` : ""}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={18} weight="duotone" className="text-[#002FA7]" />
    <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
    {subtitle && <span className="text-xs text-slate-500 ml-2">— {subtitle}</span>}
  </div>
);

const SubHeader = ({ icon: Icon, title, badge, tone, onMore }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <Icon size={16} weight="duotone" className={tone === "rose" ? "text-rose-600" : "text-[#002FA7]"} />
      <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
      {typeof badge === "number" && badge > 0 && (
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${tone === "rose" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
          {badge}
        </span>
      )}
    </div>
    {onMore && (
      <button onClick={onMore} className="text-xs uppercase tracking-wider font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1">
        Details <ArrowRight size={10} weight="bold" />
      </button>
    )}
  </div>
);

const AttentionRow = ({ testId, label, value, onClick, tone }) => (
  <li data-testid={testId} onClick={onClick}
    className={`flex items-center justify-between py-1.5 px-2 -mx-2 cursor-pointer hover:bg-slate-50 transition-colors`}>
    <span className="text-slate-700">{label}</span>
    <span className={`text-sm font-bold tabular-nums px-2 py-0.5 rounded-none ${
      value > 0 ? (tone === "rose" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800") : "bg-emerald-100 text-emerald-700"
    }`}>
      {fmtNum(value || 0)}
    </span>
  </li>
);

const RiskRow = ({ testId, label, value, onClick }) => (
  <li data-testid={testId} onClick={onClick}
    className="flex items-center justify-between py-1.5 px-2 -mx-2 cursor-pointer hover:bg-rose-50/40 transition-colors">
    <span className="text-slate-700">{label}</span>
    <span className={`text-sm font-bold tabular-nums px-2 py-0.5 ${
      value > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
    }`}>
      {fmtNum(value || 0)}
    </span>
  </li>
);

const PerformerCard = ({ testId, label, person, icon: Icon }) => (
  <div data-testid={testId} className="border border-slate-200 p-3 bg-slate-50/40">
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
    {person ? (
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-slate-900 flex items-center gap-1.5">
            <Icon size={14} weight="duotone" className="text-amber-500" />
            {person.name}
          </div>
          <div className="text-[11px] text-slate-500">{person.won_count} wins · {person.leads} leads</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-emerald-700 tabular-nums">{fmtINR(person.won_value)}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">won value</div>
        </div>
      </div>
    ) : (
      <div className="text-sm text-slate-400">No data yet</div>
    )}
  </div>
);

const ProductList = ({ testId, label, rows, valueKey, money }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1">
      <Package size={11} weight="duotone" /> {label}
    </div>
    {rows.length === 0 ? (
      <div className="text-xs text-slate-400 italic">No data yet</div>
    ) : (
      <ul className="space-y-1">
        {rows.slice(0, 3).map((r, i) => (
          <li key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-100">
            <span className="truncate pr-2 text-slate-700">{r.product_name}</span>
            <span className="font-bold tabular-nums shrink-0">{money ? fmtINR(r[valueKey]) : fmtNum(r[valueKey])}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center py-8 text-sm text-slate-400 italic">{text}</div>
);

export default MDDashboard;
